// Every duration and spring the app moves with, as the iOS app names them (FW-010, after
// `BibleStandard/Sources/App/Models/MotionTokens.swift`, `MotionToken.curve` and
// `MotionInterval.seconds` as iOS ships them at cc8dce5b8; BS-WEB-FEAT-10 step 3 brought them up from
// the inventory's Appendix 1). A component names a token and never writes a curve, so the web and the
// phone feel the same.
//
// Per frame only transform and opacity move (BS-IOS-TASK-627, DESIGN.md "Motion"): a token drives a
// translate, a scale, a rotation or an opacity, never a blur, a shadow, a mask or a size.
//
// The conversion (mass 1): SwiftUI `.spring(response: r, dampingFraction: ζ)` is
// `stiffness = (2π / r)²`, `damping = 4π·ζ / r`. `.spring(duration: d, bounce: b)` is the same spring
// with `r = d` and `ζ = 1 − b`; `.snappy(duration: d)` is `bounce 0.15`. SwiftUI's eased curves are
// Core Animation's: easeIn (0.42, 0, 1, 1), easeOut (0, 0, 0.58, 1), easeInOut (0.42, 0, 0.58, 1).
//
// Reduce Motion keeps iOS's one rule: what would move takes `fade` (easeOut 0.2s) instead. Never
// nothing: `motionFor` and `useMotion` hand it over, and base.css does the same for CSS.
//
// The CSS half is `src/styles/motion.css`, generated from this file (`--motion-<token>`: a
// duration and an easing, a spring sampled into `linear()`); `tokens.test.ts` fails when it drifts
// and rewrites it with `UPDATE_MOTION_CSS=1 npx vitest run src/motion/tokens.test.ts`.
import { useReducedMotion } from "motion/react";

type Bezier = readonly [number, number, number, number];

/** A token as iOS states it. */
export type SpringCurve = { kind: "spring"; response: number; dampingFraction: number };
export type TweenCurve = { kind: "tween"; duration: number; ease: Bezier };
export type TokenCurve = SpringCurve | TweenCurve;

const BEZIER = {
  easeIn: [0.42, 0, 1, 1],
  easeOut: [0, 0, 0.58, 1],
  easeInOut: [0.42, 0, 0.58, 1],
} as const satisfies Record<string, Bezier>;

const spring = (response: number, dampingFraction: number): SpringCurve => ({ kind: "spring", response, dampingFraction });
/** `.spring(duration:bounce:)`: the same spring with response = duration, ζ = 1 − bounce. */
const springDuration = (duration: number, bounce: number): SpringCurve => spring(duration, 1 - bounce);
/** `.snappy(duration:)`: SwiftUI's snappy is a spring with a bounce of 0.15. */
const snappy = (duration: number): SpringCurve => springDuration(duration, 0.15);
const easeOut = (duration: number): TweenCurve => ({ kind: "tween", duration, ease: BEZIER.easeOut });
const easeIn = (duration: number): TweenCurve => ({ kind: "tween", duration, ease: BEZIER.easeIn });
const easeInOut = (duration: number): TweenCurve => ({ kind: "tween", duration, ease: BEZIER.easeInOut });
const curve = (x1: number, y1: number, x2: number, y2: number, duration: number): TweenCurve => ({ kind: "tween", duration, ease: [x1, y1, x2, y2] });

/** `MotionToken.curve`, every default as shipped (MotionTokens.swift), in its order, and `pocketGlide`. */
export const TOKENS = {
  press: snappy(0.18),
  settle: spring(0.32, 1.0),
  sheet: spring(0.5, 0.88),
  release: springDuration(0.5, 0.2),
  fade: easeOut(0.2),
  /** SwiftUI's `.default` (a smooth spring: duration 0.5, no bounce). */
  standard: springDuration(0.5, 0),
  ambient: easeInOut(16),
  curve: curve(0.32, 0.72, 0, 1, 0.22),
  curveBrief: curve(0.32, 0.72, 0, 1, 0.18),
  selectionRing: spring(0.35, 0.85),
  flashFade: easeOut(0.24),
  flashAppear: easeOut(0.06),
  lineRise: curve(0.22, 1, 0.36, 1, 0.6),
  highlightLine: curve(0.22, 1, 0.36, 1, 0.35),
  highlightFill: curve(0.22, 1, 0.36, 1, 0.4),
  ledgerLight: easeInOut(0.45),
  lightPass: easeInOut(1.1),
  selectionSheetArrive: spring(0.38, 0.84),
  selectionSheetLeave: easeIn(0.22),
  selectionSheetFold: spring(0.42, 0.86),
  selectionSheetPopover: spring(0.32, 0.88),
  selectionSheetLift: spring(0.38, 0.84),
  selectionSheetPenRaise: spring(0.28, 0.7),
  selectionSheetExpand: spring(0.4, 0.86),
  pageChromeShrink: spring(0.46, 0.88),
  pageChromeZoom: spring(0.42, 0.9),
  searchStageLift: spring(0.44, 0.86),
  searchStageReturn: spring(0.36, 0.92),
  /** SwiftUI's `.snappy` (duration 0.5, bounce 0.15). */
  snappyDefault: snappy(0.5),
  snappy22: snappy(0.22),
  snappy24: snappy(0.24),
  easeOut08: easeOut(0.08),
  easeOut16: easeOut(0.16),
  easeOut18: easeOut(0.18),
  easeOut22: easeOut(0.22),
  easeOut27: easeOut(0.27),
  easeIn22: easeIn(0.22),
  easeInOut18: easeInOut(0.18),
  easeInOut20: easeInOut(0.2),
  easeInOut22: easeInOut(0.22),
  easeInOut24: easeInOut(0.24),
  easeInOut28: easeInOut(0.28),
  pullSettle: springDuration(0.3, 0),
  railPress: spring(0.24, 0.7),
  scrubArm: spring(0.24, 0.82),
  chipFlash: spring(0.3, 0.55),
  scrollTo: spring(0.32, 0.88),
  canonExpand: spring(0.36, 0.86),
  navigatorTray: spring(0.42, 0.86),
  testamentJump: spring(0.5, 0.9),
  capsuleRoll: spring(0.36, 0.86),
  setAside: spring(0.35, 0.85),
  libraryPane: easeOut(0.14),
  libraryDial: curve(0.22, 1, 0.36, 1, 0.95),
  librarySwitch: spring(0.5, 1.0),
  libraryCoversRipple: spring(0.45, 0.9),
  libraryCoversTurn: spring(0.5, 1.0),
  libraryShelfDim: spring(0.35, 1.0),
  libraryBand: easeOut(0.15),
  libraryShelfRaise: spring(0.26, 0.74),
  libraryShelfSettle: spring(0.3, 1.0),
  libraryShelfPull: spring(0.42, 1.0),
  findNarrow: springDuration(0.42, 0),
  authorsStageWipe: spring(0.62, 1.0),
  authorsStageLetter: spring(0.46, 0.9),
  authorsStageFollow: spring(0.42, 1.0),
  authorsStageLeave: easeOut(0.22),
  bookOpenTimeline: spring(0.9, 0.9),
  bookOpenDock: spring(0.32, 1.0),
  bookOpenSpringBack: spring(0.42, 0.86),
  bookOpenRise: spring(0.3, 1.0),
  bookOpenSwing: spring(0.25, 0.9),
  pocketPop: spring(0.44, 0.8),
  pocketPick: spring(0.38, 0.84),
  pocketTurn: spring(0.42, 0.86),
  /** The pick's row gliding on after the finger lets go, on the turn's spring (MotionBookOpen.swift, FS-428 D4). */
  pocketGlide: spring(0.42, 0.86),
  gridListMorph: spring(0.42, 0.86),
  gridListWordsIn: easeOut(0.2),
  gridListWordsOut: easeOut(0.08),
  bookObjectPress: spring(0.32, 0.72),
  bookObjectFollow: spring(0.25, 0.8),
  livingRoomRise: easeOut(0.8),
  livingRoomSettle: easeInOut(0.8),
  livingRoomLoop: easeInOut(1),
  fillIn: spring(0.3, 1.0),
  homePlanTurn: springDuration(0.4, 0),
  homeAddMorph: springDuration(0.35, 0.15),
  homeCaption: easeOut(0.2),
  homeBring: springDuration(0.5, 0),
  homeSeat: springDuration(0.45, 0.2),
  homeOpen: springDuration(0.5, 0),
  fellowshipPhase: spring(0.34, 1.0),
  fellowshipSignIn: spring(0.42, 1.0),
  fellowshipWelcome: spring(0.6, 1.0),
  fellowshipIntroCoverRise: spring(0.62, 1.0),
  fellowshipIntroGather: spring(0.5, 1.0),
} as const satisfies Record<string, TokenCurve>;

export type MotionToken = keyof typeof TOKENS;

/**
 * `MotionInterval.seconds` (MotionTokens.swift), in its order. Most are seconds; the rises, radii,
 * raises, slides and lifts in points are CSS px (`libraryShelfDrift` is px a second), the spacings,
 * tips and the fill are degrees, and the dip, the dims, `libraryShelfTurnEnd` and `libraryShelfLift`
 * (shares of the climb) and the scales are fractions: the units are the accessors' own
 * (MotionSurfaces.swift, MotionBookOpen.swift).
 */
export const INTERVAL = {
  /** An arrival's per-row delay: 0.03 since it plays on every visit (FS-393 D6). */
  staggerStep: 0.03,
  /** How far below its place a room's row starts its arrival (FS-393 D6). */
  roomEntranceRise: 6,
  bannerDelay: 0.25,
  selectionSheetPenStep: 0.035,
  flashHold: 0.9,
  lineStep: 0.085,
  markHold: 0.3,
  libraryDialDelay: 0.42,
  libraryRippleStep: 0.03,
  libraryCoversStagger: 0.04,
  librarySwitchPageRise: 36,
  librarySwitchCaseRise: 16,
  libraryCoverRise: 14,
  libraryOpenLead: 0.26,
  libraryOpenSpan: 0.36,
  libraryOpenFade: 0.12,
  libraryShelfRaiseBy: 14,
  libraryShelfSlideOut: 18,
  libraryShelfPortraitTip: 3,
  libraryShelfDrift: 12,
  libraryShelfDriftHold: 3,
  libraryShelfDriftResume: 4,
  libraryShelfTurnEnd: 0.8,
  libraryShelfLift: 0.8,
  libraryShelfTip: 8,
  libraryShelfTakeDim: 0.25,
  findRise: 8,
  findWave: 0.18,
  findMissDelay: 0.15,
  authorsStageLetterStep: 0.024,
  authorsStageLead: 0.12,
  /** The Authors stage keeps the old arrival step on its own token. */
  authorsStageStep: 0.04,
  pocketStep: 0.12,
  /** The pocket at FS-428's sizes (BUG-608): the dial's 126×189 books on a 228 ring. */
  pocketDialRadius: 228,
  pocketDialSpacing: 38,
  /** The carousel's 140×210 books on a 144 ring, 70 deep. */
  pocketCarouselRadius: 144,
  pocketCarouselDepth: 70,
  pocketCarouselSpacing: 50,
  pocketTakeDip: 0.05,
  bookOpenHold: 0.12,
  bookOpenFillDegrees: 110,
  livingRoomFade: 0.45,
  livingRoomLeafPeriod: 7.4,
  livingRoomPrewarm: 14,
  livingRoomSwayPeriod: 20,
  livingRoomCloudPeriod: 29,
  livingRoomThinCloudPeriod: 47,
  livingRoomShortestLoop: 0.2,
  livingRoomBreath: 10,
  livingRoomMoteLife: 14,
  livingRoomFleckLife: 11,
  livingRoomGlintLife: 2.2,
  profileDriftBreath: 0.018,
  profileDriftSway: 3,
  profileDriftTilt: 0.4,
  profileDriftBreathLeg: 7,
  profileDriftSwayLeg: 11,
  profileDriftTiltLeg: 13,
  fillInScale: 0.98,
  fellowshipRise: 6,
  fellowshipSignInShrink: 0.96,
  fellowshipWelcomeRise: 24,
  fellowshipWelcomeScale: 0.96,
  fellowshipWelcomeFormFade: 1.0,
  fellowshipWelcomeHold: 0.55,
  fellowshipIntroCoverStep: 0.05,
  fellowshipIntroCoverLift: 40,
} as const;

/** How far a row rises as it arrives (`roomEntrance(row:)`: `roomEntranceRise` on `settle`, `staggerStep` apart). */
export const RISE = INTERVAL.roomEntranceRise;

/**
 * A page dragged down to close it (`BookOpenTimeline.Drag`, BookOpenTimeline+Motion.swift): a release
 * past `closeDistance` px, or a flick down faster than `closeVelocity` px/s, closes; the page shrinks
 * `shrinkPerHeight` per screen height dragged and the dim lifts `undimPerHalfHeight` per half screen;
 * a drag the wrong way is rubber-banded as UIKit bands a scroll (dimension 400, constant 0.55).
 */
export const BOOK_OPEN_DRAG = {
  closeDistance: 120,
  closeVelocity: 800,
  shrinkPerHeight: 0.45,
  undimPerHalfHeight: 0.75,
  bandDimension: 400,
  bandConstant: 0.55,
} as const;

/** UIKit's rubber band: an overshoot of `x` px shows as `x·D·c / (D + c·x)` (`Drag.rubberband`). */
export function rubberBand(overshoot: number, dimension: number = BOOK_OPEN_DRAG.bandDimension, constant: number = BOOK_OPEN_DRAG.bandConstant): number {
  return (overshoot * dimension * constant) / (dimension + constant * overshoot);
}

/** The delay of the n-th thing in an arrival (iOS: row × `staggerStep`). */
export const stagger = (index: number) => index * INTERVAL.staggerStep;

// ---- Motion (the library) transitions ----

export type SpringTransition = { type: "spring"; stiffness: number; damping: number; mass: 1; velocity?: number };
export type TweenTransition = { type: "tween"; duration: number; ease: [number, number, number, number]; delay?: number };
export type TokenTransition = SpringTransition | TweenTransition;

const round = (n: number, places = 3) => Math.round(n * 10 ** places) / 10 ** places;

/** SwiftUI's spring as Motion's: stiffness (2π / r)², damping 4π·ζ / r, mass 1. */
export function springOf(response: number, dampingFraction: number): SpringTransition {
  return { type: "spring", stiffness: round((2 * Math.PI / response) ** 2), damping: round((4 * Math.PI * dampingFraction) / response), mass: 1 };
}

function transitionOf(token: TokenCurve): TokenTransition {
  return token.kind === "spring" ? springOf(token.response, token.dampingFraction) : { type: "tween", duration: token.duration, ease: [...token.ease] };
}

/** Every token as a Motion transition: `transition={MOTION.settle}`. */
export const MOTION = Object.fromEntries(Object.entries(TOKENS).map(([k, v]) => [k, transitionOf(v)])) as { readonly [K in MotionToken]: TokenTransition };

/** A token's transition, or `fade` under Reduce Motion (iOS: a 0.2s crossfade, never nothing). */
export function motionFor(token: MotionToken, reduced: boolean | null | undefined, extra?: { delay?: number; velocity?: number }): TokenTransition {
  const base = reduced ? MOTION.fade : MOTION[token];
  if (!extra) return base;
  const out: TokenTransition & { delay?: number } = { ...base };
  if (extra.delay !== undefined) out.delay = extra.delay;
  if (extra.velocity !== undefined && out.type === "spring" && !reduced) out.velocity = extra.velocity;
  return out;
}

/** The token's transition for this reader: `fade` when they ask for less motion. */
export function useMotion(token: MotionToken, extra?: { delay?: number; velocity?: number }): TokenTransition {
  return motionFor(token, useReducedMotion(), extra);
}

// ---- The springs themselves (for keyframes, CSS, and motion read outside Motion) ----

/**
 * Where a spring is `t` seconds after starting `from` toward `to` with `velocity` (units/s): the
 * damped harmonic oscillator, solved exactly (mass 1).
 */
export function springAt(response: number, dampingFraction: number, t: number, from = 0, to = 1, velocity = 0): number {
  const w0 = (2 * Math.PI) / response;
  const z = dampingFraction;
  const x0 = from - to;
  let x: number;
  if (z < 1) {
    const wd = w0 * Math.sqrt(1 - z * z);
    x = Math.exp(-z * w0 * t) * (x0 * Math.cos(wd * t) + ((velocity + z * w0 * x0) / wd) * Math.sin(wd * t));
  } else if (z === 1) {
    x = Math.exp(-w0 * t) * (x0 + (velocity + w0 * x0) * t);
  } else {
    const s = w0 * Math.sqrt(z * z - 1);
    const r1 = -z * w0 + s;
    const r2 = -z * w0 - s;
    const c2 = (velocity - r1 * x0) / (r2 - r1);
    const c1 = x0 - c2;
    x = c1 * Math.exp(r1 * t) + c2 * Math.exp(r2 * t);
  }
  return to + x;
}

/** How long a spring takes to come to rest: within `epsilon` of the way and staying there. */
export function springSettle(response: number, dampingFraction: number, opts: { from?: number; to?: number; velocity?: number; epsilon?: number } = {}): number {
  const { from = 0, to = 1, velocity = 0, epsilon = 0.001 } = opts;
  const span = Math.max(Math.abs(to - from), Math.abs(velocity) * response * 0.2, 1e-6);
  const step = 1 / 240;
  let last = 0;
  for (let t = 0; t < 10; t += step) if (Math.abs(springAt(response, dampingFraction, t, from, to, velocity) - to) > epsilon * span) last = t;
  return round(last + step, 4);
}

/** A spring sampled into keyframes (values and their times 0…1), for a path Motion can't spring. */
export function springKeyframes(token: MotionToken, opts: { from: number; to: number; velocity?: number; samples?: number }): { values: number[]; times: number[]; duration: number } {
  const t: TokenCurve = TOKENS[token];
  if (t.kind !== "spring") throw new Error(`${token} is not a spring`);
  const duration = springSettle(t.response, t.dampingFraction, opts);
  const n = opts.samples ?? 32;
  const values: number[] = [];
  const times: number[] = [];
  for (let i = 0; i <= n; i++) {
    const at = (i / n) * duration;
    values.push(round(springAt(t.response, t.dampingFraction, at, opts.from, opts.to, opts.velocity ?? 0), 5));
    times.push(round(i / n, 5));
  }
  values[values.length - 1] = opts.to;
  return { values, times, duration };
}

/**
 * The tile taking a closed book (`pocketTake`, MotionBookOpen.swift): one spring to 1 on
 * `bookOpenDock`, pushed down at the start with −dip·(2π / response)·e, so it dips 5% and comes back
 * on the same curve. As scale keyframes from 1 to 1.
 */
export function pocketTake(samples = 32) {
  const dock = TOKENS.bookOpenDock;
  const push = INTERVAL.pocketTakeDip * ((2 * Math.PI) / dock.response) * Math.E;
  return springKeyframes("bookOpenDock", { from: 1, to: 1, velocity: -push, samples });
}

// ---- The CSS half (src/styles/motion.css) ----

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replace(/(\d+)/g, "-$1");

/** A token as CSS: `<duration> <easing>`, a spring sampled into `linear()`. */
export function cssOf(token: MotionToken): { duration: number; easing: string } {
  const t: TokenCurve = TOKENS[token];
  if (t.kind === "tween") return { duration: Math.round(t.duration * 1000), easing: `cubic-bezier(${t.ease.join(", ")})` };
  const duration = springSettle(t.response, t.dampingFraction);
  const n = 40;
  const points: string[] = [];
  for (let i = 0; i <= n; i++) points.push(String(round(springAt(t.response, t.dampingFraction, (i / n) * duration), 4)));
  points[n] = "1";
  return { duration: Math.round(duration * 1000), easing: `linear(${points.join(", ")})` };
}

/** The generated stylesheet: `--motion-<token>`, `--motion-<token>-duration`, `--motion-<token>-easing`. */
export function motionCss(): string {
  const lines = [
    "/*",
    " * Generated from src/motion/tokens.ts (the iOS Motion tokens, FW-010). Do not edit by hand:",
    " * UPDATE_MOTION_CSS=1 npx vitest run src/motion/tokens.test.ts rewrites it.",
    " * Use: `transition: transform var(--motion-settle);` or `animation: x var(--motion-settle);`.",
    " * Under reduced motion base.css turns every transition into opacity on --motion-fade.",
    " */",
    ":root {",
  ];
  for (const name of Object.keys(TOKENS) as MotionToken[]) {
    const { duration, easing } = cssOf(name);
    const k = kebab(name);
    lines.push(`  --motion-${k}-duration: ${duration}ms;`, `  --motion-${k}-easing: ${easing};`, `  --motion-${k}: var(--motion-${k}-duration) var(--motion-${k}-easing);`);
  }
  lines.push(`  --motion-stagger-step: ${Math.round(INTERVAL.staggerStep * 1000)}ms;`, `  --motion-rise: ${RISE}px;`, "}", "");
  return lines.join("\n");
}
